import { formatMoney } from '../utils/currency';
// Servicio de utilidades para B2B
class B2BUtilsService {
  // Estados de pedido
  ORDER_STATUSES = {
    PENDING: 'PENDING',
    FINALIZED: 'FINALIZED',
    CANCELLED: 'CANCELLED'
  };

  // Estados de pago
  PAYMENT_STATUSES = {
    UNPAID: 'UNPAID',
    PARTIAL: 'PARTIAL',
    PAID: 'PAID',
    OVERDUE: 'OVERDUE'
  };

  // Estados de empresa
  COMPANY_STATUSES = {
    UP_TO_DATE: 'UP_TO_DATE',
    PENDING_DEBT: 'PENDING_DEBT',
    OVERDUE_DEBT: 'OVERDUE_DEBT'
  };

  // Métodos de pago
  PAYMENT_METHODS = {
    CASH: 'CASH',
    TRANSFER: 'TRANSFER',
    CHECK: 'CHECK',
    CARD: 'CARD'
  };

  // Obtener opciones de estados de pedido
  getOrderStatusOptions() {
    return [
      { value: this.ORDER_STATUSES.PENDING, label: 'Pendiente' },
      { value: this.ORDER_STATUSES.FINALIZED, label: 'Finalizado' },
      { value: this.ORDER_STATUSES.CANCELLED, label: 'Cancelado' }
    ];
  }

  // Obtener opciones de estados de pago
  getPaymentStatusOptions() {
    return [
      { value: this.PAYMENT_STATUSES.UNPAID, label: 'Sin pagar' },
      { value: this.PAYMENT_STATUSES.PARTIAL, label: 'Pago parcial' },
      { value: this.PAYMENT_STATUSES.PAID, label: 'Pagado' },
      { value: this.PAYMENT_STATUSES.OVERDUE, label: 'Vencido' }
    ];
  }

  // Obtener opciones de estados de empresa
  getCompanyStatusOptions() {
    return [
      { value: this.COMPANY_STATUSES.UP_TO_DATE, label: 'Al día' },
      { value: this.COMPANY_STATUSES.PENDING_DEBT, label: 'Deuda pendiente' },
      { value: this.COMPANY_STATUSES.OVERDUE_DEBT, label: 'Deuda vencida' }
    ];
  }

  // Obtener opciones de métodos de pago
  getPaymentMethodOptions() {
    return [
      { value: this.PAYMENT_METHODS.CASH, label: 'Efectivo' },
      { value: this.PAYMENT_METHODS.TRANSFER, label: 'Transferencia' },
      { value: this.PAYMENT_METHODS.CHECK, label: 'Cheque' },
      { value: this.PAYMENT_METHODS.CARD, label: 'Tarjeta' }
    ];
  }

  // Obtener color del estado de pedido
  getOrderStatusColor(status) {
    switch (status) {
      case this.ORDER_STATUSES.PENDING:
        return 'warning';
      case this.ORDER_STATUSES.FINALIZED:
        return 'success';
      case this.ORDER_STATUSES.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  }

  // Obtener color del estado de pago
  getPaymentStatusColor(status) {
    switch (status) {
      case this.PAYMENT_STATUSES.UNPAID:
        return 'error';
      case this.PAYMENT_STATUSES.PARTIAL:
        return 'warning';
      case this.PAYMENT_STATUSES.PAID:
        return 'success';
      case this.PAYMENT_STATUSES.OVERDUE:
        return 'error';
      default:
        return 'default';
    }
  }

  // Obtener color del estado de empresa
  getCompanyStatusColor(status) {
    switch (status) {
      case this.COMPANY_STATUSES.UP_TO_DATE:
        return 'success';
      case this.COMPANY_STATUSES.PENDING_DEBT:
        return 'info';
      case this.COMPANY_STATUSES.OVERDUE_DEBT:
        return 'error';
      default:
        return 'default';
    }
  }

  // Obtener texto del estado de pedido
  getOrderStatusText(status) {
    switch (status) {
      case this.ORDER_STATUSES.PENDING:
        return 'Pendiente';
      case this.ORDER_STATUSES.FINALIZED:
        return 'Finalizado';
      case this.ORDER_STATUSES.CANCELLED:
        return 'Cancelado';
      default:
        return 'Desconocido';
    }
  }

  // Obtener texto del estado de pago
  getPaymentStatusText(status) {
    switch (status) {
      case this.PAYMENT_STATUSES.UNPAID:
        return 'Sin pagar';
      case this.PAYMENT_STATUSES.PARTIAL:
        return 'Pago parcial';
      case this.PAYMENT_STATUSES.PAID:
        return 'Pagado';
      case this.PAYMENT_STATUSES.OVERDUE:
        return 'Vencido';
      default:
        return status || 'Desconocido';
    }
  }

  // Obtener texto del estado de empresa
  getCompanyStatusText(status, t = null) {
      switch (status) {
        case this.COMPANY_STATUSES.UP_TO_DATE:
          return t('up_to_date');
        case this.COMPANY_STATUSES.PENDING_DEBT:
          return t('pending_debt');
        case this.COMPANY_STATUSES.OVERDUE_DEBT:
          return t('overdue_debt');
        default:
          return t('unknown_status');
      }
  }

  // Obtener texto del método de pago
  getPaymentMethodText(method) {
    switch (method) {
      case this.PAYMENT_METHODS.CASH:
        return 'Efectivo';
      case this.PAYMENT_METHODS.TRANSFER:
        return 'Transferencia';
      case this.PAYMENT_METHODS.CHECK:
        return 'Cheque';
      case this.PAYMENT_METHODS.CARD:
        return 'Tarjeta';
      default:
        return 'Desconocido';
    }
  }

  // Formatear monto como moneda
  formatCurrency(amount) {
    if (amount === null || amount === undefined) return '$0';
    return formatMoney(amount);
  }

  // Formatear fecha
  formatDate(date) {
    if (!date) return '';
    return new Date(date).toLocaleDateString('es-ES');
  }

  // Formatear fecha y hora
  formatDateTime(date) {
    if (!date) return '';
    return new Date(date).toLocaleString('es-ES');
  }

  // Calcular fecha de vencimiento
  calculateDueDate(orderDate, creditDays) {
    if (!orderDate || !creditDays) return null;
    
    const date = new Date(orderDate);
    date.setDate(date.getDate() + creditDays);
    return date.toISOString();
  }

  // Validar si una fecha está vencida
  isOverdue(dueDate) {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  }

  getBillingOrderStatusColor(status, dueDate) {
    const overdue = this.isOverdue(dueDate);

    switch (status) {
      case this.PAYMENT_STATUSES.PAID:
        return 'success';
      case this.PAYMENT_STATUSES.PARTIAL:
        return overdue ? 'error' : 'info';
      case this.PAYMENT_STATUSES.UNPAID:
        return overdue ? 'error' : 'info';
      case this.PAYMENT_STATUSES.OVERDUE:
        return 'error';
      default:
        return overdue ? 'error' : 'default';
    }
  }

  getBillingOrderStatusText(status, dueDate, t = null) {
    const overdue = this.isOverdue(dueDate);
    const tx = t || ((key) => key);

    switch (status) {
      case this.PAYMENT_STATUSES.PAID:
        return tx('paid');
      case this.PAYMENT_STATUSES.PARTIAL:
        return overdue ? tx('partial_payment_overdue') : tx('partial_payment');
      case this.PAYMENT_STATUSES.UNPAID:
        return overdue ? tx('unpaid_overdue') : tx('pending_debt');
      case this.PAYMENT_STATUSES.OVERDUE:
        return tx('overdue_debt');
      default:
        return overdue ? tx('overdue_debt') : (status || tx('unknown_status'));
    }
  }

  // Calcular días hasta vencimiento
  getDaysUntilDue(dueDate) {
    if (!dueDate) return null;
    
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  }

  // Generar número de pedido
  generateOrderNumber() {
    const timestamp = Date.now();
    return `ORD-${timestamp}`;
  }

  // Generar número de estado de cuenta
  generateStatementNumber(clientId) {
    const timestamp = Date.now();
    return `EST-${clientId}-${timestamp}`;
  }

  // Validar datos de pedido
  validateOrderData(orderData) {
    const errors = {};

    if (!orderData.clientId) {
      errors.clientId = 'El cliente es requerido';
    }

    if (!orderData.branchId) {
      errors.branchId = 'La sucursal es requerida';
    }

    if (!orderData.orderDate) {
      errors.orderDate = 'La fecha del pedido es requerida';
    }

    if (!orderData.deliveryDate) {
      errors.deliveryDate = 'La fecha de entrega es requerida';
    }

    if (!orderData.creditDays || orderData.creditDays <= 0) {
      errors.creditDays = 'Los días de crédito deben ser mayores a 0';
    }

    if (!orderData.items || orderData.items.length === 0) {
      errors.items = 'Debe agregar al menos un producto';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Validar datos de pago
  validatePaymentData(paymentData) {
    const errors = {};

    if (!paymentData.amount || paymentData.amount <= 0) {
      errors.amount = 'El monto debe ser mayor a 0';
    }

    if (!paymentData.date) {
      errors.date = 'La fecha es requerida';
    }

    if (!paymentData.method) {
      errors.method = 'El método de pago es requerido';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Calcular resumen de pedidos
  calculateOrdersSummary(orders) {
    const summary = {
      totalOrders: orders.length,
      totalAmount: 0,
      totalPaid: 0,
      totalPending: 0,
      pending: 0,
      finalized: 0,
      cancelled: 0
    };

    orders.forEach(order => {
      summary.totalAmount += order.totalAmount || 0;
      summary.totalPaid += order.paidAmount || 0;
      summary.totalPending += (order.totalAmount || 0) - (order.paidAmount || 0);

      switch (order.status) {
        case this.ORDER_STATUSES.PENDING:
          summary.pending++;
          break;
        case this.ORDER_STATUSES.FINALIZED:
          summary.finalized++;
          break;
        case this.ORDER_STATUSES.CANCELLED:
          summary.cancelled++;
          break;
      }
    });

    return summary;
  }

  // Calcular resumen de empresas
  calculateCompaniesSummary(companies) {
    const summary = {
      totalCompanies: companies.length,
      totalAmount: 0,
      totalPaid: 0,
      totalPending: 0,
      upToDate: 0,
      pendingDebt: 0,
      overdueDebt: 0
    };

    companies.forEach(company => {
      summary.totalAmount += company.totalAmount || 0;
      summary.totalPaid += company.paidAmount || 0;
      summary.totalPending += company.pendingAmount || 0;

      switch (company.status) {
        case this.COMPANY_STATUSES.UP_TO_DATE:
          summary.upToDate++;
          break;
        case this.COMPANY_STATUSES.PENDING_DEBT:
          summary.pendingDebt++;
          break;
        case this.COMPANY_STATUSES.OVERDUE_DEBT:
          summary.overdueDebt++;
          break;
      }
    });

    return summary;
  }
}

export default new B2BUtilsService();
